import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';

export default function LevelSelectPage() {
  const { user, updateUserLevel } = useAuth();
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState(user?.level || null);
  const [loading, setLoading] = useState(false);

  const levels = [
    {
      id: 'B1',
      name: 'B1 Preliminary (PET)',
      description: 'Intermediate level - can understand the main points of clear standard input on familiar matters.',
      descriptionEs: 'Nivel intermedio - puede entender los puntos principales de textos claros sobre temas familiares.',
      skills: ['Basic conversations', 'Simple texts', 'Everyday situations'],
      color: 'green'
    },
    {
      id: 'B2',
      name: 'B2 First (FCE)',
      description: 'Upper-intermediate level - can interact with a degree of fluency and spontaneity.',
      descriptionEs: 'Nivel intermedio-alto - puede interactuar con fluidez y espontaneidad.',
      skills: ['Complex texts', 'Professional topics', 'Academic contexts'],
      color: 'blue'
    },
    {
      id: 'C1',
      name: 'C1 Advanced (CAE)',
      description: 'Advanced level - can use language flexibly and effectively for social, academic and professional purposes.',
      descriptionEs: 'Nivel avanzado - puede usar el idioma de forma flexible y efectiva para propósitos sociales, académicos y profesionales.',
      skills: ['Nuanced expression', 'Academic writing', 'Complex discussions'],
      color: 'purple'
    },
    {
      id: 'C2',
      name: 'C2 Proficiency (CPE)',
      description: 'Proficiency level - can understand virtually everything heard or read with ease.',
      descriptionEs: 'Nivel de dominio - puede entender prácticamente todo lo que lee o escucha con facilidad.',
      skills: ['Near-native fluency', 'Sophisticated discourse', 'Professional mastery'],
      color: 'amber'
    }
  ];

  const handleConfirm = async () => {
    if (!selectedLevel) {
      toast.error('Por favor, selecciona un nivel');
      return;
    }

    setLoading(true);
    try {
      await updateUserLevel(selectedLevel);
      toast.success(`¡Nivel ${selectedLevel} seleccionado!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al actualizar el nivel');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      green: isSelected ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-green-300',
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300',
      purple: isSelected ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-300',
      amber: isSelected ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'
    };
    return colors[color];
  };

  const getBadgeColor = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      amber: 'bg-amber-100 text-amber-800'
    };
    return colors[color];
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
            Elige tu nivel de Cambridge
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Selecciona el examen que estás preparando. Podrás cambiar de nivel en cualquier momento desde tu perfil.
          </p>
        </div>

        {/* Level Cards */}
        <div className="grid gap-4 mb-8">
          {levels.map((level, index) => (
            <div
              key={level.id}
              onClick={() => setSelectedLevel(level.id)}
              className={`p-6 border-2 bg-white cursor-pointer transition-all duration-300 animate-fade-in-up ${
                getColorClasses(level.color, selectedLevel === level.id)
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`level-${level.id.toLowerCase()}-card`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`level-badge ${getBadgeColor(level.color)}`}>
                      {level.id}
                    </span>
                    <h3 className="font-semibold text-lg text-slate-900">{level.name}</h3>
                  </div>
                  <p className="text-slate-600 mb-2">{level.description}</p>
                  <p className="text-sm text-slate-500 italic mb-3">{level.descriptionEs}</p>
                  <div className="flex flex-wrap gap-2">
                    {level.skills.map((skill) => (
                      <span 
                        key={skill}
                        className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ml-4 flex-shrink-0 ${
                  selectedLevel === level.id 
                    ? `border-${level.color}-500 bg-${level.color}-500` 
                    : 'border-slate-300'
                }`}>
                  {selectedLevel === level.id && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Confirm Button */}
        <div className="flex justify-center animate-fade-in-up animation-delay-400">
          <Button
            onClick={handleConfirm}
            disabled={!selectedLevel || loading}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-10 py-6 text-lg font-medium transition-all hover:scale-105 disabled:opacity-50"
            data-testid="confirm-level-btn"
          >
            {loading ? 'Guardando...' : 'Confirmar Nivel'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
}
